import React, { useState } from "react";
import styles from "./AchievementCard.module.scss";
import type { Achievement } from "../../../../types/achievements";
import noImageFallback from "../../../../assets/no-image.jpg";

/**
 * AchievementCard Component
 * 
 * Displays individual achievement information with icon, title, description,
 * unlock status, rarity indicator, and metadata.
 * 
 */

/**
 * Props for AchievementCard component
 * 
 */
export interface AchievementCardProps {
  achievement: Achievement;
  gameName?: string; // Optional game name for display
}

/**
 * Determines rarity tier based on global unlock percentage
 * 
 * 
 * @param percentage - Global unlock percentage (0-100)
 * @returns Rarity tier classification
 */
export function getRarityTier(percentage: number): 'common' | 'rare' | 'very-rare' {
  if (percentage > 50) return 'common';
  if (percentage > 10) return 'rare';
  return 'very-rare';
}

/**
 * Maps provider codes to display names
 * 
 */
const PROVIDER_DISPLAY_NAMES: Record<string, string> = {
  steam: 'Steam',
  psn: 'PlayStation',
  xbox: 'Xbox',
  nintendo: 'Nintendo',
  epic: 'Epic Games',
};

/**
 * AchievementCard Component
 * 
 */
export const AchievementCard: React.FC<AchievementCardProps> = ({
  achievement,
  gameName,
}) => {
  const [imageError, setImageError] = useState(false);

  // Determine which icon to display based on unlock status
  const iconUrl = achievement.unlocked 
    ? achievement.iconUrl 
    : achievement.iconGrayUrl || achievement.iconUrl;

  // Format unlock date for display
  const unlockedDate = achievement.unlockedAt
    ? new Date(achievement.unlockedAt).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : null;

  // Determine rarity tier if global unlock percentage is available
  const rarity = achievement.globalUnlockPercentage !== undefined
    ? getRarityTier(achievement.globalUnlockPercentage)
    : null;

  // Get provider display name
  const providerName = PROVIDER_DISPLAY_NAMES[achievement.provider] || achievement.provider;

  // Handle image load errors with fallback
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (!imageError) {
      setImageError(true);
      e.currentTarget.src = noImageFallback;
      console.warn(`Failed to load icon for achievement: ${achievement.name}`);
    }
  };

  return (
    <article 
      className={`${styles.card} ${achievement.unlocked ? styles.unlocked : styles.locked}`}
      aria-label={`Achievement: ${achievement.name}`}
    >
      <div className={styles.iconContainer}>
        <img
          src={iconUrl || noImageFallback}
          alt={`${achievement.name} achievement icon`}
          className={styles.icon}
          loading="lazy"
          decoding="async"
          onError={handleImageError}
        />
        {!achievement.unlocked && (
          <div className={styles.lockOverlay} aria-label="Locked achievement">
            <span className={styles.lockIcon}>🔒</span>
          </div>
        )}
      </div>

      <div className={styles.content}>
        <div className={styles.header}>
          <h3 className={styles.title}>{achievement.name}</h3>
          {achievement.unlocked && (
            <span className={styles.unlockedBadge} aria-label="Unlocked">
              ✓
            </span>
          )}
        </div>

        <p className={styles.description}>{achievement.description}</p>

        <div className={styles.metadata}>
          <div className={styles.metaRow}>
            <span className={styles.metaLabel}>Platform:</span>
            <span className={styles.metaValue}>{providerName}</span>
          </div>

          {gameName && (
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Game:</span>
              <span className={styles.metaValue}>{gameName}</span>
            </div>
          )}

          {achievement.unlocked && unlockedDate && (
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Unlocked:</span>
              <span className={styles.metaValue}>{unlockedDate}</span>
            </div>
          )}

          {achievement.globalUnlockPercentage !== undefined && (
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Rarity:</span>
              <span className={styles.metaValue}>
                {achievement.globalUnlockPercentage.toFixed(1)}% of players
              </span>
            </div>
          )}
        </div>

        {rarity && (
          <div className={styles.badges}>
            <span 
              className={`${styles.rarityBadge} ${styles[`rarity-${rarity}`]}`}
              aria-label={`Rarity: ${rarity}`}
            >
              {rarity === 'very-rare' ? 'Very Rare' : rarity.charAt(0).toUpperCase() + rarity.slice(1)}
            </span>
          </div>
        )}
      </div>
    </article>
  );
};

export default AchievementCard;
